import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { buildOverrideMap, lessonsForDate } from '@/lib/schedule';
import type { LessonOverride } from '@/types';
import type { ScheduleStudent } from '@/pages/dashboard/components/WeekScheduleCard';

interface TodayLessonsCardProps {
  students: ScheduleStudent[];
  /** 오늘 수업일지가 이미 작성된 학생 id 목록 */
  writtenStudentIds: string[];
  overrides?: LessonOverride[];
}

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

function todayLabel(): string {
  return DAY_LABELS[new Date().getDay()];
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function TodayLessonsCard({
  students,
  writtenStudentIds,
  overrides = [],
}: TodayLessonsCardProps) {
  const day = todayLabel();
  const date = todayStr();
  const written = new Set(writtenStudentIds);

  const overrideMap = useMemo(() => buildOverrideMap(overrides), [overrides]);
  const lessons = useMemo(
    () => lessonsForDate(students, date, overrideMap, overrides),
    [students, date, overrideMap, overrides],
  );

  const doneCount = lessons.filter((l) => written.has(l.studentId)).length;
  const movedInCount = lessons.filter((l) => l.isMovedIn).length;

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-timer-flash-line"></i>
          </span>
          <h2 className="text-base font-bold text-foreground-950">오늘 수업 ({day})</h2>
        </div>
        {lessons.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-primary-100 px-3 py-1 font-semibold text-primary-700">
              수업 {lessons.length}건
            </span>
            {movedInCount > 0 && (
              <span className="rounded-full bg-primary-100 px-3 py-1 font-semibold text-primary-700">
                날짜 변경 {movedInCount}건
              </span>
            )}
            <span className="rounded-full bg-background-200 px-3 py-1 font-semibold text-foreground-600">
              일지 {doneCount}/{lessons.length}
            </span>
          </div>
        )}
      </div>

      {lessons.length === 0 ? (
        <div className="mt-5 flex flex-col items-center gap-2 rounded-lg border border-dashed border-background-300 py-10">
          <i className="ri-cup-line text-2xl text-foreground-400"></i>
          <p className="text-sm text-foreground-600">오늘은 예정된 수업이 없어요.</p>
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {lessons.map((l) => {
            const isDone = written.has(l.studentId);
            return (
              <div
                key={`${l.studentId}-${l.isMovedIn ? 'in' : 'reg'}`}
                className={`flex flex-col gap-3 rounded-lg border p-4 ${
                  l.isMovedIn
                    ? 'border-primary-300 bg-primary-50'
                    : 'border-background-200 bg-background-100'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 truncate text-sm font-bold text-foreground-950">
                      {l.name}
                      {l.isMovedIn && (
                        <span className="shrink-0 rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-semibold text-primary-700">
                          날짜 변경
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-foreground-500">
                      <i className={l.isTimeChanged ? 'ri-equalizer-line text-accent-600' : 'ri-time-line'}></i>
                      {l.start ? `${l.start}${l.end ? `~${l.end}` : ''}` : '시간 미입력'}
                      {l.isTimeChanged && (
                        <span className="font-semibold text-accent-700">· 변경됨</span>
                      )}
                    </p>
                  </div>
                  {isDone ? (
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-primary-100 px-2.5 py-0.5 text-[11px] font-semibold text-primary-700">
                      <i className="ri-check-line"></i>
                      작성 완료
                    </span>
                  ) : (
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-accent-100 px-2.5 py-0.5 text-[11px] font-semibold text-accent-800">
                      <i className="ri-time-line"></i>
                      미작성
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/lessons/new?student=${l.studentId}&date=${date}`}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary-500 px-3 py-2 text-xs font-semibold text-background-50 transition hover:bg-primary-600 whitespace-nowrap"
                  >
                    <i className="ri-edit-box-line"></i>
                    {isDone ? '추가 작성' : '수업일지 작성'}
                  </Link>
                  <Link
                    to={`/students/${l.studentId}`}
                    className="flex items-center justify-center gap-1 rounded-md border border-background-300 px-3 py-2 text-xs font-semibold text-foreground-700 transition hover:bg-background-50 whitespace-nowrap"
                  >
                    <i className="ri-user-3-line"></i>
                    카드
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}